import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  compile,
  createBreadcrumbWithI18n,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { StyledButton } from '~/components/elements/StyledButton'
import { StyledCard } from '~/components/elements/StyledCard/StyledCard'
import { VStack } from '~/components/elements/VStack'
import { BrandTreeViewBox } from '~/components/features/BrandTreeViewBox'
import { CustomerAttributeTreeViewBox } from '~/components/features/CustomerAttributeTreeViewBox'
import { DataDisplayTypeAtomSwitcher } from '~/components/features/DataDisplayTypeAtomSwitcher'
import { QuestionTreeViewBox } from '~/components/features/QuestionTreeViewBox'
import { SegmentDisplaySwitcher } from '~/components/features/SegmentDisplaySwitcher'
import {
  SharedBrandGroupSelector,
  type SharedBrandGroupSelectorProps,
} from '~/components/features/SharedBrandGroupSelector'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useTurnOnAtom } from '~/hooks/jotai'
import { useRestoreOnMount } from '~/hooks/useRestoreOnMount'
import { useScrollEffect } from '~/hooks/useScrollEffect'
import { useSurveyQueryFlattenCustomerAttributes } from '~/hooks/useSurveyQuery'

import {
  brandIdsAtom,
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  isPendingTabulationAtom,
  isTabulatedAtom,
  isTabulationReadyAtom,
  questionIdsAtom,
  segmentDisplayTypeAtom,
} from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useAvailableCustomerAttributeGroups } from '../hooks/useAvailableCustomerAttributeGroups'
import { useOmitNonTabulatedColumnsFromDiff } from '../hooks/useOmitNonTabulatedColumnsFromDiff'
import { useSurveyId } from '../hooks/useSurveyId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'
import { pathKey } from '../utils'

import { AllColumnCustomerAttributeGroupsSelectButton } from './AllColumnCustomerAttributeGroupsSelectButton'
import styles from './CustomerAttributeAnalysis.css'
import { CustomerAttributeGroupCheckboxTreeList } from './CustomerAttributeGroupCheckboxTreeList'
import { CustomerAttributeTable } from './CustomerAttributeTable'
import { TabulateButton } from './TabulateButton'

export const CustomerAttributeAnalysis = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('advancedAnalysis'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const isExecuting = useTabulationQuery()
  const customerAttributeGroups = useAvailableCustomerAttributeGroups()
  const setBrandIds = useSetAtom(brandIdsAtom)
  const suspendTabulation = useTurnOnAtom(isPendingTabulationAtom)
  const handleBrandGroupChange = React.useCallback<
    EH<SharedBrandGroupSelectorProps, 'onChange'>
  >(() => {
    setBrandIds([])
    suspendTabulation()
  }, [setBrandIds, suspendTabulation])
  const customerAttributes = useSurveyQueryFlattenCustomerAttributes()

  useOmitNonTabulatedColumnsFromDiff()

  const restoreParams = useRestoreParams()
  useRestoreOnMount(restoreParams, isTabulationReadyAtom)

  const isTabulated = useAtomValue(isTabulatedAtom)
  useScrollEffect(isTabulated)

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isExecuting}>
      <VStack spacing={4} verticalAppearance="stretch-scroll">
        <Link
          className={styles.settingsLink}
          to={compile('customerAttributeSettings', { surveyId })}
        >
          <StyledButton fillMode="outline" icon="gear">
            {t('edit_customized_segments')}
          </StyledButton>
        </Link>

        {0 < customerAttributeGroups.length && (
          <>
            <LabelingContainer
              label={t('customized_segments_group_column_direction_selection')}
            >
              <StyledCard
                bodyHeight={200}
                headerContent={<AllColumnCustomerAttributeGroupsSelectButton />}
                headerJustify="start"
                title={t('customized_segments')}
              >
                <CustomerAttributeGroupCheckboxTreeList />
              </StyledCard>
            </LabelingContainer>

            <Box />

            <LabelingContainer label={t('brand_group')}>
              <SharedBrandGroupSelector onChange={handleBrandGroupChange} />
            </LabelingContainer>

            <LabelingContainer label={t('display_brand_selection')}>
              <BrandTreeViewBox
                brandIdsAtom={brandIdsAtom}
                onSelectedBrandIdChange={suspendTabulation}
              />
            </LabelingContainer>

            <LabelingContainer label={t('select_questionnaire')}>
              <QuestionTreeViewBox
                questionIdsAtom={questionIdsAtom}
                onSelectedQuestionIdChange={suspendTabulation}
              />
            </LabelingContainer>

            {0 < customerAttributes.length && (
              <LabelingContainer label={t('customized_segments_selection')}>
                <CustomerAttributeTreeViewBox
                  customerAttributeIdsAtom={customerAttributeIdsAtom}
                  onSelectedCustomerAttributeIdsChange={suspendTabulation}
                />
              </LabelingContainer>
            )}

            {/*
              SharedBrandGroupSelectorは他画面でも使う共通コンポーネントで、
              withSuffixを渡していないため接尾辞の対象に含めない。表示切替・並び替え・表だけを囲む。
            */}
            <AmplitudeProvider eventNameSuffix="_customer attribute">
              <LabelingContainer label={t('switch_view')}>
                <HStack spacing={4}>
                  <DataDisplayTypeAtomSwitcher
                    dataDisplayTypeAtom={dataDisplayTypeAtom}
                  />

                  <SegmentDisplaySwitcher
                    segmentDisplayTypeAtom={segmentDisplayTypeAtom}
                    withBoth={true}
                    withSummary={true}
                  />
                </HStack>
              </LabelingContainer>

              <TabulateButton className={styles.tabulateButton} />

              <CustomerAttributeTable />
            </AmplitudeProvider>
          </>
        )}
      </VStack>
    </PageContainer>
  )
}
